import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Home as HomeIcon, CalendarClock, Check, LogOut } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/analytics";
import { DeleteButton } from "@/components/DeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const EMPTY = { business_name: "", rent_amount: "", move_in_date: "", move_out_date: null };

export default function Rent() {
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() {
    try {
      const [t, p] = await Promise.all([api.get("/rent/tenants"), api.get("/rent/payments")]);
      setTenants(t.data); setPayments(p.data);
    } catch { /* */ }
  }

  async function addTenant(e) {
    e.preventDefault();
    if (!form.move_in_date) return toast.error("Select a move-in date.");
    setLoading(true);
    try {
      await api.post("/rent/tenants", {
        business_name: form.business_name, rent_amount: Number(form.rent_amount),
        move_in_date: form.move_in_date,
        move_out_date: form.move_out_date || null,
      });
      toast.success("Tenant added."); setForm(EMPTY); setAddOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  async function moveOut(t) {
    try { await api.patch(`/rent/tenants/${t.id}`, { move_out_date: format(new Date(), "yyyy-MM-dd") }); toast.success("Move-out recorded."); load(); }
    catch { toast.error("Failed."); }
  }

  async function delTenant(t) {
    try { await api.delete(`/rent/tenants/${t.id}`); toast.success("Tenant and its payments deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete."); }
  }

  function isPaid(tenantId, month) {
    return payments.find((p) => p.tenant_id === tenantId && p.month === month + 1 && p.year === year && p.paid);
  }

  async function togglePaid(t, month) {
    const already = isPaid(t.id, month);
    try {
      await api.post("/rent/payments", {
        tenant_id: t.id, tenant_name: t.business_name, amount: t.rent_amount,
        month: month + 1, year, paid: !already,
      });
      toast.success(!already ? `Marked paid for ${MONTHS[month]}` : `Unmarked ${MONTHS[month]}`); load();
    } catch { toast.error("Failed."); }
  }

  const nextDue = format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1), "MMMM d, yyyy");

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Rent Management</h1>
          <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><CalendarClock className="h-3 w-3" /> Rent is due the 1st of every month. Next due: {nextDue}.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="rounded-full" data-testid="rent-add-tenant"><Plus className="mr-1 h-4 w-4" /> Add Tenant</Button>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Label className="text-sm">Year</Label>
        <div className="flex gap-1">
          {[year - 1, year, year + 1].map((y) => (
            <button key={y} onClick={() => setYear(y)} data-testid={`rent-year-${y}`}
              className={`rounded-md px-3 py-1 text-sm ${y === year ? "bg-primary text-white" : "bg-secondary text-secondary-foreground"}`}>{y}</button>
          ))}
        </div>
      </div>

      {tenants.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">
          <HomeIcon className="mx-auto mb-3 h-10 w-10 opacity-40" /> No tenants yet. Add your first tenant.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {tenants.map((t) => (
            <div key={t.id} className="rounded-xl border border-border/60 bg-card p-5" data-testid={`rent-tenant-${t.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-heading font-bold">{t.business_name}</h3>
                  <p className="text-sm text-muted-foreground">{money(t.rent_amount)}/mo • Moved in {t.move_in_date}{t.move_out_date ? ` • Moved out ${t.move_out_date}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                {!t.move_out_date && <Button size="sm" variant="outline" className="rounded-full" onClick={() => moveOut(t)} data-testid={`rent-moveout-${t.id}`}><LogOut className="mr-1 h-3 w-3" /> Record Move-Out</Button>}
                  <DeleteButton onConfirm={() => delTenant(t)} label={`tenant ${t.business_name}`} testId={`rent-delete-${t.id}`} variant="text" />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-12">
                {MONTHS.map((m, idx) => {
                  const paid = isPaid(t.id, idx);
                  return (
                    <button key={m} onClick={() => togglePaid(t, idx)} data-testid={`rent-month-${t.id}-${idx}`}
                      className={`flex flex-col items-center rounded-lg border py-2 text-xs transition-colors ${paid ? "border-primary bg-primary text-white" : "border-border bg-background hover:border-primary"}`}>
                      <span>{m}</span>
                      {paid ? <Check className="mt-1 h-3 w-3" /> : <span className="mt-1 h-3 w-3 rounded-full border border-muted-foreground/40" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Tenant</DialogTitle></DialogHeader>
          <form onSubmit={addTenant} className="space-y-4">
            <div className="space-y-2"><Label>Business name</Label><Input data-testid="rent-form-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Rent amount ($)</Label><Input type="number" step="0.01" data-testid="rent-form-amount" value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Move-in date</Label>
              <Input type="date" data-testid="rent-form-movein" value={form.move_in_date} onChange={(e) => setForm({ ...form, move_in_date: e.target.value })} required />
            </div>
            <p className="text-xs text-muted-foreground">Move-out can be recorded later from the tenant card.</p>
            <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="rent-form-submit">{loading ? "Saving..." : "Add Tenant"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
